import React, { useState, useCallback } from 'react';

import { planFanOut, rowsFromResponse } from '../../../lib/themeFanOut';

/**
 * "Apply this theme to my other player preferences" -- the fill-only fan-out.
 *
 * Flow: list the client's preferences, plan (pure), show the admin
 * exactly what will and will not change, and only then write. The
 * confirm step is not ceremony -- fan-out is an N-write operation
 * against preferences the admin is not looking at, so it states the
 * blast radius before causing it.
 *
 * Fill-only means already-themed preferences are skipped. Those are
 * listed by name, because "12 updated" while three silently kept an old
 * brand is the failure this feature would otherwise ship with.
 *
 * Writes run sequentially rather than in parallel: these are writes to
 * a shared backend, and a burst of concurrent edits buys nothing on an
 * operation an admin runs once.
 */
export default function ThemeFanOut ({ theme, prefID, isDirty, disabled, disabledReason, actions, onWorkspaceAction }) {
    const [phase, setPhase] = useState('idle');
    // Opt-in, decided at the review step where the affected preferences
    // are named -- not a mode chosen before you can see what it costs.
    const [replaceExisting, setReplaceExisting] = useState(false);
    const [plan, setPlan] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const reset = useCallback(() => {
        setReplaceExisting(false);
        setPhase('idle');
        setPlan(null);
        setResult(null);
        setError(null);
    }, []);

    const handlePlan = useCallback(() => {
        setPhase('planning');
        setError(null);
        actions.listPreferences(function (err, resp) {
            if (err) {
                setError('Could not load your other player preferences.');
                setPhase('idle');
                return;
            }
            const next = planFanOut(rowsFromResponse(resp), theme, prefID);
            setPlan(next);
            setPhase('confirming');
        });
    }, [actions, theme, prefID]);

    const handleApply = useCallback(() => {
        setPhase('writing');

        // Fill-only by default; the skipped bucket is included only when
        // explicitly opted into, and it carries the same already-themed
        // properties so no re-planning is needed.
        const targets = replaceExisting
            ? plan.targets.concat(plan.skipped)
            : plan.targets.slice();
        const written = [];
        const failed = [];

        function next () {
            if (!targets.length) {
                setResult({ written, failed, replaced: replaceExisting });
                setPhase('done');
                onWorkspaceAction({
                    type: 'snack',
                    snack: failed.length
                        ? { type: 'error', message: 'Applied to ' + written.length + ' of ' + (written.length + failed.length) + ' preferences' }
                        : { type: 'success', message: 'Theme applied to ' + written.length + ' preference' + (written.length === 1 ? '' : 's') },
                });
                return;
            }
            const target = targets.shift();
            actions.fanOutSave(target, function (err) {
                if (err) failed.push(target); else written.push(target);
                next();
            });
        }

        next();
    }, [plan, actions, onWorkspaceAction, replaceExisting]);

    const writeCount = plan
        ? plan.targets.length + (replaceExisting ? plan.skipped.length : 0)
        : 0;

    if (disabled) {
        return (
            <section className="theme-fanout theme-fanout--disabled">
                <h4 className="theme-fanout__title">Apply to your other player preferences</h4>
                <p className="theme-fanout__lede">{disabledReason}</p>
            </section>
        );
    }

    return (
        <section className="theme-fanout">
            <h4 className="theme-fanout__title">Apply to your other player preferences</h4>
            <p className="theme-fanout__lede">
                Copies this theme into preferences that don&rsquo;t have one, so every
                embed shares a look. Preferences with a theme of their own are never
                overwritten.
            </p>

            {phase === 'idle' ? (
                <div className="theme-fanout__actions">
                    <button
                        type="button"
                        className="btn btn-default"
                        onClick={handlePlan}
                    >
                        Review changes&hellip;
                    </button>
                    {isDirty ? (
                        // NOT a gate. Fan-out applies the theme on screen,
                        // pending edits included, so there is nothing to wait
                        // for -- but this preference itself is still unsaved,
                        // and saying so beats letting someone discover it.
                        <span className="text-muted theme-fanout__note">
                            Includes your unsaved changes — remember to save this
                            preference too.
                        </span>
                    ) : null}
                    {error ? <span className="text-danger theme-fanout__note">{error}</span> : null}
                </div>
            ) : null}

            {phase === 'planning' ? (
                <p className="text-muted theme-fanout__note">
                    <i className="fa fa-circle-notch fa-spin fa-fw"/> Checking your preferences&hellip;
                </p>
            ) : null}

            {phase === 'confirming' && plan ? (
                <div>
                    {plan.targets.length === 0 ? (
                        <p className="text-muted">
                            {plan.skipped.length
                                ? 'Every other player preference already has a theme of its own. Replace them below to push this one through.'
                                : 'Nothing to update — you have no other player preferences.'}
                        </p>
                    ) : (
                        <div className="theme-fanout__list">
                            <strong>
                                {plan.targets.length} preference{plan.targets.length === 1 ? '' : 's'} will
                                get this theme
                            </strong>
                            <ul>
                                {plan.targets.map(t => (
                                    <li key={t.prefID}>{t.name || <em>Untitled</em>}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {plan.skipped.length ? (
                        <div className="theme-fanout__skipped">
                            <strong>
                                {plan.skipped.length} already {plan.skipped.length === 1 ? 'has' : 'have'} a
                                theme of {plan.skipped.length === 1 ? 'its' : 'their'} own
                            </strong>
                            <ul>
                                {plan.skipped.map(sk => (
                                    <li key={sk.prefID}>{sk.name || <em>Untitled</em>}</li>
                                ))}
                            </ul>
                            <label className="theme-fanout__replace">
                                <input
                                    type="checkbox"
                                    checked={replaceExisting}
                                    onChange={ev => setReplaceExisting(ev.target.checked)}
                                />
                                {' '}Replace {plan.skipped.length === 1 ? 'its theme' : 'their themes'} too
                                <span className="text-muted">
                                    {' '}— overwrites the {plan.skipped.length === 1 ? 'look' : 'looks'} listed above.
                                    This is how a re-brand reaches preferences that are already themed.
                                </span>
                            </label>
                        </div>
                    ) : null}

                    {plan.unreadable.length ? (
                        <p className="text-muted theme-fanout__note">
                            {plan.unreadable.length} preference
                            {plan.unreadable.length === 1 ? '' : 's'} could not be read and
                            {plan.unreadable.length === 1 ? ' was' : ' were'} left untouched.
                        </p>
                    ) : null}

                    <div className="theme-fanout__actions">
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={writeCount === 0}
                            onClick={handleApply}
                        >
                            Apply to {writeCount} preference{writeCount === 1 ? '' : 's'}
                            {replaceExisting && plan.skipped.length
                                ? ' (' + plan.skipped.length + ' replaced)'
                                : ''}
                        </button>
                        <button type="button" className="btn btn-link" onClick={reset}>Cancel</button>
                    </div>
                </div>
            ) : null}

            {phase === 'writing' ? (
                <p className="text-muted theme-fanout__note">
                    <i className="fa fa-circle-notch fa-spin fa-fw"/> Applying&hellip;
                </p>
            ) : null}

            {phase === 'done' && result ? (
                <div>
                    <p>
                        Applied to <strong>{result.written.length}</strong> preference
                        {result.written.length === 1 ? '' : 's'}
                        {result.replaced ? ', replacing themes that were already set' : ''}.
                    </p>

                    {/* Skipped preferences stay visible AFTER the run, not just
                        in the confirmation. Fill-only means a re-brand does not
                        reach them, so this list is the only prompt to go and
                        re-theme those deliberately. */}
                    {plan && !result.replaced ? renderSkipped(plan.skipped) : null}

                    {result.failed.length ? (
                        <div className="alert alert-danger">
                            <strong>{result.failed.length} could not be saved.</strong>
                            <ul>
                                {result.failed.map(f => (
                                    <li key={f.prefID}>{f.name || <em>Untitled</em>}</li>
                                ))}
                            </ul>
                        </div>
                    ) : null}

                    <div className="theme-fanout__actions">
                        <button type="button" className="btn btn-default" onClick={reset}>Done</button>
                    </div>
                </div>
            ) : null}
        </section>
    );
}

/**
 * Preferences fill-only skipped, listed by name.
 *
 * Shown at confirmation AND after the run: fill-only never overwrites an
 * existing theme, so a re-brand silently does not reach these. Naming
 * them is what turns that from a surprise into a to-do.
 */
function renderSkipped (skipped) {
    if (!skipped || !skipped.length) return null;
    return (
        <div className="theme-fanout__skipped">
            <strong>
                {skipped.length} already {skipped.length === 1 ? 'has' : 'have'} a theme
                and {skipped.length === 1 ? 'was' : 'were'} left alone
            </strong>
            <ul>
                {skipped.map(s => (
                    <li key={s.prefID}>{s.name || <em>Untitled</em>}</li>
                ))}
            </ul>
            <span className="text-muted">
                To give {skipped.length === 1 ? 'it' : 'them'} this theme, open each one
                and apply it there.
            </span>
        </div>
    );
}
